/**
 * Client-side resolution of hierarchical notification preferences.
 *
 * The API stores one row per (scope, channel): a positive opt-in (`muted: false`) or a mute override
 * (`muted: true`). A row at a broader scope is inherited by narrower scopes, and the NEAREST covering row
 * wins per channel — segment over section over instance. So a whole-dial opt-in can be cancelled by muting
 * one section, and that section re-enabled by opting back in on a single segment.
 *
 * These helpers compute the effective on/off state at a scope and plan the minimal write to flip it.
 *
 * KEY-592: the list may also carry the caller's whole-dial rows from a Group Key Dial ABOVE this dial
 * (`inheritedFromInstanceId`). Following a group follows its children, so those rows sit one level broader than
 * this dial's own whole-dial scope — level -1 — and switching a channel off at the dial scope becomes a mute here,
 * exactly as it would under a whole-dial opt-in. They are never written from this dial.
 */
import type { NotificationSubscriptionDto, SetSubscriptionRequest, SubscriptionMode } from "@keydials/api-client";

export type Channel = "InApp" | "Email" | "Sms" | "Push";
/**
 * Every channel a row can carry. Push (KEY-684) is listed even where the server has it switched off, so a Push
 * row left from before the switch still counts as subscribed and is still turned off by Unsubscribe — whether
 * the menu OFFERS it is {@link offeredChannels}' call.
 */
export const CHANNELS: { key: Channel; label: string }[] = [
  { key: "InApp", label: "In-app" },
  { key: "Email", label: "Email" },
  { key: "Sms", label: "SMS" },
  { key: "Push", label: "Push to my phone" },
];

/**
 * The channels the menu shows at a scope. Push only when the server has it on (`pushEnabled`, from
 * `Features:PushNotifications`) — or when it is already on here, so an opt-in made before the switch went off can
 * still be switched off rather than sitting there invisibly. Same one-way rule as SMS without a phone (KEY-453).
 */
export function offeredChannels(
  rows: NotificationSubscriptionDto[],
  scope: Scope,
  pushEnabled: boolean,
): { key: Channel; label: string }[] {
  return CHANNELS.filter((c) => c.key !== "Push" || pushEnabled || channelOn(rows, scope, c.key));
}

/** The default channel a one-click "Subscribe" opts you into (Email/SMS are added via the dropdown). */
export const DEFAULT_CHANNEL: Channel = "InApp";

export interface Scope {
  sectionKey: string | null;
  segmentKey: string | null;
}

const level = (s: Scope): number => (s.segmentKey ? 2 : s.sectionKey ? 1 : 0);

/** Does a stored row apply to (cover) the given scope — i.e. is the row at an equal-or-broader scope? */
function covers(row: NotificationSubscriptionDto, scope: Scope): boolean {
  if (row.sectionKey == null) {
    return true; // instance scope covers everything
  }
  if (row.segmentKey == null) {
    return row.sectionKey === scope.sectionKey; // section covers itself + its segments
  }
  return row.sectionKey === scope.sectionKey && row.segmentKey === scope.segmentKey; // segment covers only itself
}

const rowLevel = (row: NotificationSubscriptionDto): number =>
  row.inheritedFromInstanceId ? -1 : row.segmentKey ? 2 : row.sectionKey ? 1 : 0;

/** The nearest covering row for a channel, or null if none — `maxLevel` excludes rows at/above a level. */
function nearest(
  rows: NotificationSubscriptionDto[],
  scope: Scope,
  channel: Channel,
  maxLevel = 99,
): NotificationSubscriptionDto | null {
  let best: NotificationSubscriptionDto | null = null;
  for (const row of rows) {
    if (row.channel !== channel || !covers(row, scope) || rowLevel(row) > maxLevel) {
      continue;
    }
    if (!best || rowLevel(row) > rowLevel(best)) {
      best = row;
    }
  }
  return best;
}

/** Effective on/off for a single channel at a scope (nearest covering row, on unless muted). */
export function channelOn(rows: NotificationSubscriptionDto[], scope: Scope, channel: Channel): boolean {
  const row = nearest(rows, scope, channel);
  return !!row && !row.muted;
}

/** Effective state inherited from STRICTLY broader scopes (ignoring any row exactly at this scope). */
function inheritedOn(rows: NotificationSubscriptionDto[], scope: Scope, channel: Channel): boolean {
  const row = nearest(rows, scope, channel, level(scope) - 1);
  return !!row && !row.muted;
}

/** Subscribed at this scope = any channel is effectively on. */
export function isSubscribed(rows: NotificationSubscriptionDto[], scope: Scope): boolean {
  return CHANNELS.some((c) => channelOn(rows, scope, c.key));
}

/** The set of channels effectively on at this scope. */
export function activeChannels(rows: NotificationSubscriptionDto[], scope: Scope): Set<Channel> {
  return new Set(CHANNELS.map((c) => c.key).filter((c) => channelOn(rows, scope, c)));
}

/** True when the effective state at this scope comes from a broader scope (not a row at this scope). */
export function isInherited(rows: NotificationSubscriptionDto[], scope: Scope): boolean {
  return CHANNELS.some((c) => {
    const here = nearest(rows, scope, c.key);
    return !!here && rowLevel(here) < level(scope) && !here.muted;
  });
}

/**
 * The group this scope's notifications come from, when that is where they come from (KEY-592): the name of the
 * group whose follow is the nearest thing switching any channel on here. Null when the state is this dial's own.
 */
export function followedThroughGroup(rows: NotificationSubscriptionDto[], scope: Scope): string | null {
  for (const c of CHANNELS) {
    const here = nearest(rows, scope, c.key);
    if (here && !here.muted && here.inheritedFromInstanceId) {
      return here.inheritedFromName ?? "a group";
    }
  }
  return null;
}

/** Plan the minimal write to set one channel on/off at a scope, given the inherited state. */
export function planChannel(
  rows: NotificationSubscriptionDto[],
  scope: Scope,
  channel: Channel,
  on: boolean,
): SetSubscriptionRequest {
  const inherited = inheritedOn(rows, scope, channel);
  let mode: SubscriptionMode;
  if (on) {
    mode = inherited ? "None" : "Subscribe"; // already on from above → clear local override; else opt in here
  } else {
    mode = inherited ? "Mute" : "None"; // on from above → mute here; else just clear any local opt-in
  }
  return { sectionKey: scope.sectionKey, segmentKey: scope.segmentKey, channel, mode };
}

/** Plan the writes for the one-click Subscribe button: subscribe (default channel) or unsubscribe (all). */
export function planToggleSubscribed(rows: NotificationSubscriptionDto[], scope: Scope): SetSubscriptionRequest[] {
  if (isSubscribed(rows, scope)) {
    // Turn off every channel currently effectively on at this scope.
    return CHANNELS.filter((c) => channelOn(rows, scope, c.key)).map((c) => planChannel(rows, scope, c.key, false));
  }
  return [planChannel(rows, scope, DEFAULT_CHANNEL, true)];
}

/**
 * Why a channel can't be switched on for this user, or null when it can (KEY-453).
 *
 * SMS needs a phone number on the account: without one `SmsDispatchJob` silently drops every alert, so an
 * SMS opt-in looks subscribed and delivers nothing forever. We gate the opt-in rather than let that happen.
 * The check is deliberately one-way — an existing SMS row can always be turned OFF, or the user would be
 * stuck subscribed to a channel that never arrives.
 */
export function channelBlockedReason(channel: Channel, hasPhoneNumber: boolean): string | null {
  if (channel === "Sms" && !hasPhoneNumber) {
    return "Add a phone number to your account to receive SMS alerts.";
  }
  return null;
}
